import { formatDuration, intervalToDuration, parseISO } from 'date-fns';
import type { AvatarInfo } from 'src/components/AlineAvatar';
import { toAvatarPlaceholder } from 'src/components/AlineAvatar';
import { Apollo } from 'src/gql/generated';
import { roleAvatarColor } from 'src/theme';
import type { AccountSubset } from 'src/types';

export function getWorkflowRuntimeString(
  workflow: Pick<
    Apollo.Workflow,
    'completedAt' | 'createdAt' | 'startedAt' | '__typename' | 'variant'
  >
): string {
  const isTemplateWorkflow = workflow.variant === Apollo.WorkflowVariant.Template;
  if (isTemplateWorkflow) return 'N/A';

  return formatIntervalAtHighestDimension(getWorkflowRuntimeInterval(workflow));
}

export function getWorkflowRuntimeInterval(
  workflow: Pick<Apollo.Workflow, 'completedAt' | 'createdAt' | 'startedAt' | '__typename'>
): { end: Date; start: Date } {
  return {
    end: workflow.completedAt ? parseISO(workflow.completedAt) : new Date(),
    start: workflow.startedAt ? parseISO(workflow.startedAt) : parseISO(workflow.createdAt),
  };
}

export function formatIntervalAtHighestDimension({
  end,
  start,
}: {
  end: Date;
  start: Date;
}): string {
  const duration = intervalToDuration({ end, start });
  const dimensions = [
    { unit: 'years', value: duration.years },
    { unit: 'months', value: duration.months },
    { unit: 'weeks', value: duration.weeks },
    { unit: 'days', value: duration.days },
    { unit: 'hours', value: duration.hours },
    { unit: 'minutes', value: duration.minutes },
    { unit: 'seconds', value: duration.seconds },
  ];
  const highestDimension = dimensions.find(dim => Boolean(dim.value)) ?? {
    unit: 'seconds',
    value: 0,
  };

  return formatDuration({ [highestDimension.unit]: highestDimension.value });
}

export interface DisplayWorkflowPermission extends Pick<
  Apollo.WorkflowPermission,
  'avatarColor' | 'id' | 'isLauncher' | 'roleName' | '__typename'
> {
  account: AccountSubset | null;
}

export const LAUNCHER_PLACEHOLDER_TEXT = 'Filled at launch';
export const WORKFLOW_LAUNCHER_LABEL = 'Workflow launcher';

export function getWorkflowPermissionDisplayDetails(permission: DisplayWorkflowPermission): {
  avatarInfo: AvatarInfo;
  primaryText: string;
  avatarColor?: string;
  secondaryText?: string;
} {
  if (permission.isLauncher)
    return {
      avatarColor: roleAvatarColor,
      avatarInfo: toAvatarPlaceholder(WORKFLOW_LAUNCHER_LABEL, permission.avatarColor),
      primaryText: WORKFLOW_LAUNCHER_LABEL,
      secondaryText: LAUNCHER_PLACEHOLDER_TEXT,
    };
  const roleName = permission.roleName?.trim() || null;
  if (!permission.account)
    return {
      avatarColor: roleName ? roleAvatarColor : undefined,
      avatarInfo: roleName ? toAvatarPlaceholder(roleName, permission.avatarColor) : null,
      primaryText: roleName ?? 'Unassigned',
    };

  const fallbackSecondaryText =
    permission.account.__typename === 'User' &&
    permission.account.displayName.toLowerCase() !== permission.account.email.toLowerCase()
      ? permission.account.email
      : undefined;

  return {
    avatarColor: permission.account.avatarColor,
    avatarInfo: permission.account,
    primaryText:
      permission.account.__typename === 'User'
        ? permission.account.displayName
        : permission.account.name,
    secondaryText: roleName ?? fallbackSecondaryText,
  };
}
